import React from "react";
import { Box, IconButton, makeStyles, Button } from "@material-ui/core";
import { MTableToolbar } from "material-table";
import CloseIcon from "@material-ui/icons/Close";
import SmallSearchBox from "../../common/SmallSearchBox";
import DialogTopPanel from "../../common/DialogTopPanel";
import clsx from "clsx";
import { remCalc } from "../../../utils/helpers";
import history from "../../../utils/history";
import ImportIcon from "../../icons/ImportIcon";

/** Custom component for toolbar for custom search element and "back to project" feature
 * @param {Object} props
 * @param {Array} props.projects[] projects list
 * @param {Object} props.classes generated styles and classlist
 * @param {boolean} props.editing is in editing state right now
 * @param {string} [props.activeProjectId] id of current open project
 * @param {func} props.handleClose function to close parent dialog
 * @param {Object} props.props default props for Material table toolbar
 */

const useStyles = makeStyles(theme => ({
  goBack: {
    fontWeight: "normal",
    position: "absolute",
    left: theme.spacing(2),
    top: theme.spacing(2),
    zIndex: 1,
    "& svg": {
      width: theme.spacing(3),
      height: theme.spacing(3)
    }
  },
  wrapper: {
    position: "absolute",
    bottom: 0,
    right: 24,
    marginBottom: theme.spacing(1.25)
  },
  root: ({ inline }) => ({
    paddingLeft: inline ? 0 : theme.spacing(9) + "px !important",
    borderBottom: "1px solid #eee"
  }),
  rootEditing: {
    "& .MuiButton-text": {
      display: "none"
    }
  },
  checkbox: {
    marginRight: theme.spacing(0.5),
    "& svg": {
      color: "inherit"
    }
  },
  disabledIcon: {
    "& svg": {
      background: "none !important",
      color: theme.palette.text.disabled
    }
  },
  btnLight: {
    whiteSpace: "nowrap",
    padding: theme.spacing(1),
    paddingRight: theme.spacing(1.5),
    letterSpacing: "0.1px",
    flexShrink: 0,
    lineHeight: "1rem",
    zIndex: "5",
    color: "#5F6368",
    position: "absolute",
    right: theme.spacing(3),
    top: theme.spacing(2)
  }
}));

export default function CustomToolbar({
  isApp,
  editing,
  activeProjectId,
  handleClose,
  singular,
  plural,
  pagination,
  setInfoDialog,
  handleMultipleDownload,
  plan,
  getDataPerPage,
  handleMultipleDelete,
  handleAdd,
  loading,
  upload,
  handleOpen,
  currentAction,
  setConfirmDialogState,
  setLoadingCircle,
  totalQuantity,
  setSelected,
  inline,
  addButtonDisabled,
  addId,
  ...props
}) {
  const classes = useStyles({ inline });
  const numSelected = props.selected.length;
  const count = props.data.length;

  const itemsCount = `${count} ${singular}${count > 1 ? "s" : ""}`;

  const inProgressProjects =
    singular === "project"
      ? props.renderData
          .filter(data => data.status === "In Progress")
          .map(data => data.id)
      : [];

  const isInProgress =
    singular === "project"
      ? props.selected.some(data => inProgressProjects.includes(data))
      : false;

  return (
    <Box position="relative">
      {!inline && (
        <IconButton
          className={classes.goBack}
          onClick={handleClose}
          data-cy="projects-goBack"
        >
          <CloseIcon />
        </IconButton>
      )}
      {upload?.enabled && (
        <Button
          className={classes.btnLight}
          data-cy={`${plural}-uploadBtn`}
          onClick={e => {
            if (plan.ft_spreadsheet_upload) {
              if (upload.setUploadOpen) {
                upload.setUploadOpen(true);
              } else {
                history.push(`${plural}/upload`);
              }
            } else {
              setInfoDialog({
                visible: true,
                title: "Please upgrade your plan",
                body: "Spreadsheet importing is not included in the Individual plan, please upgrade your plan to Small Business.",
                button: {
                  text: "Upgrade plan",
                  link: "/payment/select-plan"
                },
                mainButtonText: "No, thanks"
              });
            }
          }}
        >
          <ImportIcon
            style={{
              width: remCalc(20),
              height: remCalc(20),
              marginRight: remCalc(8)
            }}
          />
          Upload {plural}
        </Button>
      )}

      <MTableToolbar
        {...props}
        classes={{
          root: clsx(classes.root, { [classes.rootEditing]: editing })
        }}
      />
      {!editing && (
        <>
          <SmallSearchBox
            className={classes.wrapper}
            searchText={props.searchText}
            onSearchChanged={props.onSearchChanged}
            ariaLabel={`Search ${plural}`}
            dataCy={`${plural}-search`}
          />
          <DialogTopPanel
            isProjectTable
            isApp={isApp}
            content={
              singular === "project" ? `New ${singular}` : `Add ${singular}`
            }
            count={count}
            setSelected={setSelected}
            loading={loading}
            numSelected={numSelected}
            selected={props.selected}
            tooltipDelete={`Delete selected ${plural}`}
            tooltipDownload={`Download selected ${plural}`}
            itemsCount={itemsCount}
            iconClassName={""}
            handleOpen={handleOpen}
            getDataPerPage={getDataPerPage}
            currentAction={currentAction}
            dataAddCy={`add-new-${singular}`}
            dataDeleteCy={`delete-selected-${singular}s`}
            handleMultipleDownload={handleMultipleDownload}
            handleMultipleDelete={() =>
              handleMultipleDelete({ isInProgress: isInProgress })
            }
            handleAdd={handleAdd}
            addId={addId}
            setLoadingCircle={setLoadingCircle}
            addButtonDisabled={addButtonDisabled}
          />
        </>
      )}
      <Box mb={pagination?.enabled ? 13 : 7}></Box>
    </Box>
  );
}
